import React, { useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  shouldUseLegacyProviderCompatibility,
  TENANT_PREFERENCE_STORAGE_KEY,
} from "../../lib/authoritativeTenant";
import { setSupabaseTenantId } from "../../lib/tenantTransport";
import { Shield, Lock, Mail, ArrowRight, Loader2 } from "lucide-react";

/* ADMIN LOGIN — Direct email/password login for Super Admin
   No RNC verification needed. Goes directly to /admin dashboard. */

export const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          throw new Error("Credenciales incorrectas.");
        }
        throw new Error(error.message);
      }

      const legacyProvider = shouldUseLegacyProviderCompatibility(
        data.user?.app_metadata,
        import.meta.env.VITE_LEGACY_PROVIDER_COMPAT === "true",
      );
      const providerResult = await supabase.rpc(
        "is_current_provider_admin",
      );
      const isAuthoritativeProvider =
        !providerResult.error && providerResult.data === true;
      if (!isAuthoritativeProvider && !legacyProvider) {
        await supabase.auth.signOut();
        throw new Error(
          "Acceso denegado. Solo Super Admins pueden acceder a este panel.",
        );
      }

      setSupabaseTenantId(null);
      localStorage.removeItem(TENANT_PREFERENCE_STORAGE_KEY);
      window.location.assign("/admin");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-gray-900 p-10 rounded-3xl border border-gray-800 shadow-2xl">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-rose-600/30">
            <Shield className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-white mb-2">
            OsirisBrain
          </h2>
          <p className="text-sm text-gray-400 font-medium uppercase tracking-wider">
            Super Admin Console
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-950/50 border border-red-800 rounded-2xl text-sm text-red-400 font-medium text-center">
            {error}
          </div>
        )}

        <form className="mt-10 space-y-6" onSubmit={handleAuth}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-1.5 ml-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-gray-800 border border-gray-700 rounded-2xl text-white font-medium placeholder:text-gray-500 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all sm:text-sm"
                  placeholder="admin@osirisbrain.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-1.5 ml-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-gray-800 border border-gray-700 rounded-2xl text-white font-medium placeholder:text-gray-500 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all sm:text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="group relative flex w-full justify-center py-4 px-4 text-sm font-bold rounded-2xl text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-rose-500 transition-all active:scale-[0.98] disabled:opacity-70"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <span className="flex items-center gap-2">
                Acceder{" "}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            )}
          </button>
          <a
            href="/login"
            className="block text-center text-sm text-gray-500 hover:text-gray-300 transition-colors mt-4"
          >
            ← Volver al Login de Tenant
          </a>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
